import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// Components 
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';

import { createConsultation, getRecentNotes, deleteConsultation } from '../../../../routes/doctor/consultation';
import { setIsRefreshRecentNotesList, setRecentNotes, setSelectedConsultationNote, setConsultationId } from '../../../../redux/features/doctor/Dashboard/consultation/consultation';
import NoteRow from './NoteRow';
import { setIsSuspendedGroupPopupOpened } from '../../../../redux/features/general/navigation';
import { setNotificationMessage } from '../../../../redux/features/general/notification';

interface RecentProps {
    isMobile: boolean;
    onClose?: () => void;
}

const Recent: React.FC<RecentProps> = ({ isMobile, onClose }) => {
    const dispatch = useAppDispatch(); 
    const { t } = useTranslation(); 
    const navigate = useNavigate(); 

    const { consultationId, consultationNoteId } = useParams(); 

    const colors = useAppSelector(state => state.theme.colors); 
    const isRefreshRecentNotesList = useAppSelector(state => state.consultation.isRefreshRecentNotesList); 
    const recentNotes = useAppSelector(state => state.consultation.recentNotes); 
    const selectedConsultationNote = useAppSelector(state => state.consultation.selectedConsultationNote); 

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [sortedNotes, setSortedNotes] = useState<any[]>([]);

    const handleRecentNotes = async () => { 
        const res = await getRecentNotes() as any; 
        if (res?.data.status === 200) { 
            dispatch(setRecentNotes(res.data.consultationNotes)); 
        }
    }; 

    const handleConsultationCreation = async () => { 
        const res = await createConsultation() as any; 
        if (res?.data.status === 200) { 
            dispatch(setConsultationId(res.data.consultation._id)); 
            navigate(`/consultation/${res.data.consultation._id}/note/${res.data.consultationNote._id}`);
            if (isMobile && onClose) {
                onClose();
            }
        } else if (res?.data.status === 401 && res.data.error === 'suspendedGroup') { 
            dispatch(setIsSuspendedGroupPopupOpened(true)); 
        }
    }; 

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, note: any) => {
        setAnchorEl(event.currentTarget);
        dispatch(setSelectedConsultationNote(note));
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDeletion = async () => {
        if (!selectedConsultationNote?._id) return;

        // Store the ID of the note being deleted to check against the current URL param
        const deletedNoteId = selectedConsultationNote._id;

        handleMenuClose(); // Close the menu immediately

        // The API call deletes the consultation, which cascades to the note(s)
        const deleteRes = await deleteConsultation(selectedConsultationNote.consultationId) as any;

        if (deleteRes?.data.status === 200) {
            dispatch(setNotificationMessage(t('noteDeleted')));

            // Check if the deleted note was the one currently being viewed
            if (consultationNoteId === deletedNoteId) {
                // The current page is now invalid, so we must navigate.
                // First, fetch the fresh list of notes to find where to go next.
                const notesRes = await getRecentNotes() as any;
                const newNotesList = notesRes?.data?.consultationNotes;

                if (newNotesList && newNotesList.length > 0) {
                    // The backend sorts by last updated, so the first note is the new "current" one.
                    const nextNoteToShow = newNotesList[0];
                    // Navigate to the new most recent note
                    navigate(`/consultation/${nextNoteToShow.consultationId}/note/${nextNoteToShow._id}`);
                    // Manually update the redux store since we aren't using the refresh flag here
                    dispatch(setRecentNotes(newNotesList));
                } else {
                    // If no notes are left, it's appropriate to go to the dashboard.
                    navigate('/dashboard');
                }
            } else {
                // If we deleted a different note (not the one being viewed),
                // just trigger a refresh of the list.
                dispatch(setIsRefreshRecentNotesList(true));
            }
        } else {
            dispatch(setNotificationMessage('Error: Could not delete note.'));
        }
    };

    useEffect(() => { 
        if (consultationId) { 
            handleRecentNotes(); 
        }
    }, [consultationId]); 

    useEffect(() => { 
        if (isRefreshRecentNotesList) { 
            handleRecentNotes(); 
            dispatch(setIsRefreshRecentNotesList(false)); 
        }
    }, [isRefreshRecentNotesList]); 

    useEffect(() => {
        if (recentNotes && recentNotes.length > 0) {
            const currentNote = recentNotes.find(note => note._id === consultationNoteId);
            const otherNotes = recentNotes.filter(note => note._id !== consultationNoteId);
            const newSortedNotes = currentNote ? [currentNote, ...otherNotes] : [...recentNotes];
            setSortedNotes(newSortedNotes);
        } else {
            setSortedNotes([]);
        }
    }, [recentNotes, consultationNoteId]);

    return (
      <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', 
          width: '100%',
          height: '100%',
          fontSize: 14,
          position: 'relative', // Important for child components
      }}>
        <Box  
            sx={{ 
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center', 
                width: '90%',
                borderBottomWidth: 1,
                borderBottomStyle: 'solid', 
                marginTop: 2, 
                marginBottom: 1,
                paddingBottom: 1, 
                borderBottomColor: 'rgba(150, 150, 150, 0.3)',
            }}
        >
            <Box sx={{ color: colors?.text, flexGrow: 1, fontWeight: 'bold' }}>{t('history')}</Box>
            <Box
                onClick={handleConsultationCreation}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '6px 12px',
                    borderRadius: 1.5,
                    color: 'white',
                    backgroundColor: colors?.main,
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    flexShrink: 0,
                    '&:hover': {
                        backgroundColor: colors?.mainHover,
                    }
                }}
            >
                <AddIcon sx={{ color: 'white', fontSize: '1rem', marginRight: 0.5 }} />
                {t('newConsultation')}
            </Box>
        </Box>

        <Box sx={{
            width: '90%',
            textAlign: 'center',
            fontStyle: 'italic',
            fontSize: '0.75rem',
            color: colors?.listInfo,
            padding: '4px 0 8px 0',
        }}>
            {t('notesDeletionWarning')}
        </Box>

        { (sortedNotes.length > 0) ?
            <Box  
                sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    width: '100%',
                    flexGrow: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden', 
                    scrollbarWidth: 'thin', 
                    marginTop: 1, 
                    marginBottom: 1, 
                }}
            >
                {sortedNotes.map((item, index) => (
                    <NoteRow
                        key={item._id}
                        item={item}
                        handler={handleMenuOpen}
                        isLast={(sortedNotes.length - 1) === index}
                    />
                ))}
            </Box>
        :   
            <Box
                sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    <Box
                        sx={{
                            width: 75,
                            marginBottom: 2,
                            opacity: 0.7
                        }}
                        component="img"
                        alt="Solutions Medca Inc."
                        src={require('../../../../assets/icons/files/empty-folder.png')}
                    />
                    <Box sx={{ 
                        color: 'rgb(185, 185, 185)',
                        fontSize: 12,
                        textAlign: 'center'
                    }}>{t('noConsultationsDetected')}</Box>
                </Box>
            </Box>
        }
        
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            MenuListProps={{
                'aria-labelledby': 'basic-button',
            }}
            PaperProps={{
                style: {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    borderRadius: '8px',
                },
            }}
        >
            <MenuItem onClick={handleDeletion}>
                <ListItemIcon>
                    <RemoveCircleOutlineOutlinedIcon sx={{ color: colors?.deleteIcon }} fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('delete')} primaryTypographyProps={{ style: { color: colors?.deleteIcon, fontSize: '0.9rem' } }} />
            </MenuItem>
        </Menu>
      </Box>
  );
};

export default Recent;